import React, { ChangeEvent, useContext, useMemo, useState } from "react";
import { ONBOARDING_PHONE_GUTTER } from "features/onboarding/common/onboardingUi";
import styled, { css, useTheme } from "styled-components";
import AntSegmented from "antd/es/segmented";
import { Search, SearchX } from "lucide-react";
import Input from "common/Inputs/Input";
import Button from "common/Buttons/Button";
import EmptyIllustration from "common/Empty/EmptyIllustration";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { QUICK_CONNECT_EXCHANGES } from "@shared/common/constants";
import useSelectExchange from "@shared/features/portfolio/createAccount/useSelectExchange";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context.ts";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useBottomNavigator } from "utils/hooks/misc";
import useMedia from "utils/hooks/useMedia";
import ChangeAccountTypeMessage from "features/configuration/accounts/CreateAccount/ChangeAccountTypeMessage";
import InviteExternalUser from "features/configuration/InviteExternalUsers/InviteExternalUser.tsx";
import ApiSlotsUsage from "features/configuration/common/ApiSlotsUsage.tsx";
import NewLookExchangeCard from "features/portfolio/CreateAccount/NewLook/NewLookExchangeCard";
import NewLookConnectGuide from "features/portfolio/CreateAccount/NewLook/NewLookConnectGuide";
import {
  stepDescription,
  stepTitle,
} from "features/onboarding/common/onboardingUi";

interface NewLookSelectExchangeProps {
  isOnboarding?: boolean;
  onSelectExchange?: (primaryExchange: string) => void;
  showDemoAccountOption?: boolean;
}

const ALL_MARKETS = "__all__";
const PHONE_WIDTH = "(max-width: 559px)";
// Onboarding: the guide sits beside the grid from this viewport width.
const GUIDE_SIDE_WIDTH = "(min-width: 1100px)";
const PAGE_GUTTER = 16;
const POPULAR_COUNT = 3;

const matchesSearch = (exchange: string, search: string) =>
  exchange.toLowerCase().includes(search.trim().toLowerCase());

const NewLookSelectExchange = ({
  isOnboarding,
  onSelectExchange,
  showDemoAccountOption = true,
}: NewLookSelectExchangeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const isPhone = useMedia([PHONE_WIDTH], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { getIsFree } = useContext(ExchangesMetadataContext) || {};
  const { isDemoWorkspace } = useContext(AccountFormContext)!;
  const { isExternalConnectAccount, hideQuickConnect } =
    useContext(ExternalConnectAccountContext) || {};
  const { exchangesLoading, filteredExchanges, filtersData } =
    useSelectExchange();
  const [search, setSearch] = useState("");
  const [market, setMarket] = useState(ALL_MARKETS);

  const sortedExchanges = useMemo(
    () =>
      // Free (premium-included) exchanges first; stable otherwise.
      [...filteredExchanges].sort(
        ([first], [second]) =>
          Number(!!getIsFree?.(second)) - Number(!!getIsFree?.(first))
      ),
    [filteredExchanges, getIsFree]
  );

  const visibleExchanges = sortedExchanges.filter(
    ([exchange, tags]) =>
      matchesSearch(exchange, search) &&
      (market === ALL_MARKETS || tags.includes(market))
  );

  // Popular tiles only on wider screens: stacked on a phone they just look
  // like heavier rows, so the phone gets one consistent list.
  // One uniform grid of cards everywhere (no Popular split).
  const showSections = false;
  const popularExchanges = showSections
    ? visibleExchanges.slice(0, POPULAR_COUNT)
    : [];
  const listExchanges = showSections
    ? visibleExchanges.slice(POPULAR_COUNT)
    : visibleExchanges;

  const showQuickConnectTags = !isDemoWorkspace && !hideQuickConnect;
  // Onboarding, live path: a "How connecting works" guide next to the grid.
  // It also carries the alternatives the footer shows elsewhere.
  // The "Good to know" side panel is off for now (to be revisited).
  const showGuide = false;
  // The onboarding's phone gutter is wider than the drawer's.
  const framePadding =
    isBottomNavigator && !isExternalConnectAccount
      ? {
          padding: `0 ${
            isOnboarding ? ONBOARDING_PHONE_GUTTER : PAGE_GUTTER
          }px`,
        }
      : undefined;

  const marketOptions = [
    { label: t("create_account.new_look.all_markets"), value: ALL_MARKETS },
    ...filtersData.filtersTags.map((tag) => ({ label: tag, value: tag })),
  ];

  const clearFilters = () => {
    setSearch("");
    setMarket(ALL_MARKETS);
  };

  const heading = isOnboarding ? (
    <Heading $isPage={true}>
      <Title $isPage={true}>
        {isDemoWorkspace
          ? t("create_account.new_look.demo_title")
          : t("create_account.new_look.title")}
      </Title>
      <Subtitle $isPage={true}>
        {isDemoWorkspace
          ? t("create_account.new_look.demo_subtitle")
          : t("create_account.new_look.subtitle")}
      </Subtitle>
    </Heading>
  ) : (
    <Heading $isPage={false}>
      <Subtitle $isPage={false}>
        {isDemoWorkspace
          ? t("create_account.new_look.demo_subtitle")
          : t("create_account.new_look.subtitle")}
      </Subtitle>
      {!isExternalConnectAccount && <ApiSlotsUsage compact={true} />}
    </Heading>
  );

  const toolbar = (
    <Toolbar>
      <SearchField
        value={search}
        allowClear={true}
        placeholder={t("create_account.new_look.search_placeholder")}
        aria-label={t("create_account.new_look.search_placeholder")}
        prefix={
          <Search
            size={16}
            strokeWidth={NEW_LOOK_ICON_STROKE}
            aria-hidden={true}
          />
        }
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          setSearch(event.target.value)
        }
      />
      {marketOptions.length > 2 && (
        <MarketSegmented
          options={marketOptions}
          value={market}
          block={isPhone}
          onChange={(value) => setMarket(value as string)}
        />
      )}
    </Toolbar>
  );

  const renderCard = (
    [primaryExchange, tags]: [string, string[]],
    variant: "featured" | "row" | "tile"
  ) => (
    <NewLookExchangeCard
      key={primaryExchange}
      primaryExchange={primaryExchange}
      tags={tags}
      variant={variant}
      onSelectExchange={onSelectExchange}
      isPremiumIncluded={
        !isExternalConnectAccount && getIsFree?.(primaryExchange)
      }
      isQuickConnect={
        showQuickConnectTags && QUICK_CONNECT_EXCHANGES.has(primaryExchange)
      }
    />
  );

  const emptyState = (
    <Empty>
      <EmptyIllustration icon={SearchX} width={140} height={105} />
      <EmptyTitle>{t("create_account.new_look.no_results_title")}</EmptyTitle>
      <EmptyText>
        {t("create_account.new_look.no_results_description", {
          search: search.trim() || market,
        })}
      </EmptyText>
      <Button onClick={clearFilters}>
        {t("create_account.new_look.clear_search")}
      </Button>
    </Empty>
  );

  const guide = showGuide && (
    <GuideColumn>
      <NewLookConnectGuide
        showQuickConnect={showQuickConnectTags}
        footer={
          !isExternalConnectAccount && (
            <>
              <ApiSlotsUsage compact={true} />
              <InviteExternalUser />
              <ChangeAccountTypeMessage inline={true} />
            </>
          )
        }
      />
    </GuideColumn>
  );

  const footer = !isExternalConnectAccount && !showGuide && (
    <Footer style={framePadding} $isOnboarding={!!isOnboarding}>
      {showDemoAccountOption && (
        <ChangeAccountTypeMessage inline={!isOnboarding} />
      )}
      <InviteExternalUser />
      {isOnboarding && <ApiSlotsUsage />}
    </Footer>
  );

  const grid = (
    <LoadingSpinnerOverlay
      isLoading={exchangesLoading}
      backgroundColor={theme.colors.layoutBgColor}
      hideContentWhileLoading={true}
    >
      <GridContainer
        style={showGuide ? undefined : framePadding}
        $isLoading={exchangesLoading}
      >
        {visibleExchanges.length || exchangesLoading ? (
          <>
            {!!popularExchanges.length && (
              <Section>
                <SectionTitle>
                  {t("create_account.new_look.popular")}
                </SectionTitle>
                <FeaturedGrid>
                  {popularExchanges.map((item) => renderCard(item, "featured"))}
                </FeaturedGrid>
              </Section>
            )}
            <Section>
              {showSections && (
                <SectionTitle>
                  {t("create_account.new_look.all_exchanges")}
                </SectionTitle>
              )}
              <Grid $tiles={true}>
                {listExchanges.map((item) => renderCard(item, "tile"))}
              </Grid>
            </Section>
          </>
        ) : (
          emptyState
        )}
      </GridContainer>
    </LoadingSpinnerOverlay>
  );

  return (
    <Wrapper $isBottomNavigator={isBottomNavigator}>
      <Scroll $isBottomNavigator={isBottomNavigator}>
        <div style={framePadding}>
          {heading}
          {!showGuide && toolbar}
        </div>
        {showGuide ? (
          <GuidedLayout style={framePadding}>
            <div>
              {toolbar}
              {grid}
            </div>
            {guide}
          </GuidedLayout>
        ) : (
          grid
        )}
        {/* On a phone the footer scrolls with the list instead of being
            pinned over it. */}
        {isBottomNavigator && footer}
      </Scroll>
      {!isBottomNavigator && footer}
    </Wrapper>
  );
};

export default NewLookSelectExchange;

const Wrapper = styled.div<{ $isBottomNavigator?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${(p) =>
    p.$isBottomNavigator &&
    css`
      flex: 1;
      overflow: hidden;
      justify-content: space-between;
    `}
`;
const Scroll = styled.div<{ $isBottomNavigator?: boolean }>`
  ${(p) =>
    p.$isBottomNavigator &&
    css`
      flex: 1;
      overflow: auto;
      padding-bottom: 16px;
    `}
`;
const Heading = styled.div<{ $isPage: boolean }>`
  /* The page heading matches the walkthrough step's header. */
  margin-bottom: ${(p) => (p.$isPage ? 24 : 16)}px;
  text-align: ${(p) => (p.$isPage ? "center" : "left")};
  @media ${PHONE_WIDTH} {
    margin-bottom: 20px;
    text-align: left;
  }
`;
const Title = styled.h1<{ $isPage: boolean }>`
  margin: 0;
  ${(p) =>
    p.$isPage
      ? stepTitle
      : css`
          color: ${p.theme.colors.titleColor};
          font-size: 18px;
          line-height: 26px;
          font-weight: 520;
        `}
  @media ${PHONE_WIDTH} {
    font-size: 22px;
    line-height: 30px;
  }
`;
const Subtitle = styled.p<{ $isPage: boolean }>`
  margin: ${(p) => (p.$isPage ? "4px auto 0" : "0 0 8px")};
  max-width: 600px;
  /* Balanced wrapping only for the centred page heading; in the drawer it
     left short ragged lines. */
  text-wrap: ${(p) => (p.$isPage ? "balance" : "pretty")};
  ${(p) =>
    p.$isPage
      ? stepDescription
      : css`
          ${newLookType.body};
          color: ${getNewLookTokens(p.theme).secondary};
        `}
  @media ${PHONE_WIDTH} {
    margin: ${(p) => (p.$isPage ? "4px 0 0" : "0 0 8px")};
    font-size: 14px;
    line-height: 21px;
  }
`;
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  @media ${PHONE_WIDTH} {
    flex-direction: column;
    align-items: stretch;
  }
`;
const SearchField = styled(Input)`
  &&& {
    width: 280px;
    max-width: 100%;
    height: 36px;
    border-radius: ${NEW_LOOK_RADIUS.control}px;
    @media ${PHONE_WIDTH} {
      width: 100%;
      height: 40px;
    }
  }
`;
const MarketSegmented = styled(AntSegmented)`
  flex: none;
  .ant-segmented-item-label {
    min-height: 30px;
    line-height: 30px;
    padding: 0 12px;
    font-size: 13px;
  }
  @media ${PHONE_WIDTH} {
    .ant-segmented-item-label {
      min-height: 32px;
      line-height: 32px;
    }
  }
  /* Very narrow phones: the market names fit whole ("Futures" was cut). */
  @media (max-width: 360px) {
    .ant-segmented-item-label {
      padding: 0 6px;
      font-size: 12.5px;
    }
  }
`;
const GuidedLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media ${GUIDE_SIDE_WIDTH} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    align-items: start;
    gap: 32px;
  }
`;
const GuideColumn = styled.div`
  @media ${GUIDE_SIDE_WIDTH} {
    position: sticky;
    top: 24px;
  }
`;
const GridContainer = styled.div<{ $isLoading?: boolean }>`
  container-type: inline-size;
  min-height: ${(p) => (p.$isLoading ? 300 : 0)}px;
`;
const Section = styled.section`
  & + & {
    margin-top: 24px;
  }
`;
const SectionTitle = styled.h2`
  margin: 0 0 8px;
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const FeaturedGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr);
  @container (min-width: 480px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }
`;
const Grid = styled.div<{ $tiles?: boolean }>`
  display: grid;
  gap: ${(p) => (p.$tiles ? "12px" : "2px 16px")};
  /* Cards keep their own height, so a card with an edge tag can take extra
     room below without stretching its row neighbours. */
  align-items: ${(p) => (p.$tiles ? "start" : "stretch")};
  grid-template-columns: minmax(0, 1fr);
  margin: ${(p) => (p.$tiles ? 0 : "0 -12px")};
  @container (min-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container (min-width: 880px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;
const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 16px 32px;
`;
const EmptyTitle = styled.div`
  margin-top: 8px;
  color: ${(p) => p.theme.colors.titleColor};
  ${newLookType.panelTitle};
`;
const EmptyText = styled.div`
  margin: 4px 0 16px;
  max-width: 360px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13.5px;
  line-height: 20px;
`;
const Footer = styled.div<{ $isOnboarding: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  ${(p) =>
    p.$isOnboarding &&
    css`
      max-width: 640px;
      width: 100%;
      margin: 32px auto 0;
    `}
  &:empty {
    display: none;
  }
`;
